import React from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Row } from 'react-bootstrap';

import { Strings } from './constants';
import SummaryItem from './SummaryItem';
import SignupForm from './SignupForm';

class Summary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      revenueOnline: parseFloat(this.props.answers[0].value),
      revenueTotal: parseFloat(this.props.answers[1].value),
      averageOrderValue: parseFloat(this.props.answers[2].value),
      websiteSessionsMthly: parseFloat(this.props.answers[3].value),
      grossProfitMargin: 27,

      websiteSessionsMthlySliderValue: 40,
      conversionRateSliderValue: 15,
      onlineOrdersSliderValue: 3,
      averageOrderValueSliderValue: 20,
      grossProfitMarginSliderValue: 10
    };

    const derivedValues = this.calculateDerivedValues(this.state);
    this.state = {
      ...this.state,
      ...derivedValues
    };

    this.state.onlineOrdersSliderValue = this.state.revenueOnline <= 0
      ? 3 
      : this.state.conversionRate > 0 && this.state.websiteSessionsMthly > 0
        ? null
        : 61;

    this.changeValue = this.changeValue.bind(this);
    this.updateSliderValue = this.updateSliderValue.bind(this);
    this.calculatePotentialValues = this.calculatePotentialValues.bind(this);
    this.calculateDerivedValues = this.calculateDerivedValues.bind(this);
  }

  changeValue(newValue) {
    this.setState(state => {
      const calculatedValues = this.calculateDerivedValues({
        ...state,
        ...newValue
      });

      return {
        ...calculatedValues,
        ...newValue
      };
    });
  }

  updateSliderValue(sliderValue) {
    this.setState(sliderValue);
  }

  calculateDerivedValues({revenueOnline, averageOrderValue, websiteSessionsMthly, grossProfitMargin}) {
    const monthlyRevenueOnline = revenueOnline / 12;
    const conversionRate = this._calculateConversionRate(monthlyRevenueOnline, averageOrderValue, websiteSessionsMthly);
    const onlineOrders = this._calculateOnlineOrders(monthlyRevenueOnline, averageOrderValue, websiteSessionsMthly, conversionRate);
    const profit = this._calculateProfit(monthlyRevenueOnline, grossProfitMargin);

    return {
      monthlyRevenueOnline,
      conversionRate,
      onlineOrders,
      profit
    };
  }

  calculatePotentialValues() {
    const state = this.state;
    const potentialValues = {
      futureWebsiteSessionsMthly: this._calculatePotentialValue(state.websiteSessionsMthly, state.websiteSessionsMthlySliderValue),
      futureConversionRate: this._calculatePotentialValue(state.conversionRate, state.conversionRateSliderValue),
      futureAverageOrderValue: this._calculatePotentialValue(state.averageOrderValue, state.averageOrderValueSliderValue),
      futureGrossProfitMargin: this._calculatePotentialValue(state.grossProfitMargin, state.grossProfitMarginSliderValue)
    };

    const futureOnlineOrders = this._calculateFutureOnlineOrders({ ...state, ...potentialValues });
    const futureRevenueOnline = futureOnlineOrders * potentialValues.futureAverageOrderValue;
    const futureProfit = Math.round(futureRevenueOnline * (potentialValues.futureGrossProfitMargin / 100));
    
    return {
      ...potentialValues,
      futureOnlineOrders,
      futureRevenueOnline,
      futureProfit
    };
  }

  _calculateFutureOnlineOrders({revenueOnline, futureConversionRate, futureWebsiteSessionsMthly, onlineOrders, revenueTotal, onlineOrdersSliderValue, averageOrderValue}) {
    if (revenueOnline > 0) {
      if (futureConversionRate > 0 && futureWebsiteSessionsMthly > 0) {
        return futureWebsiteSessionsMthly * (futureConversionRate / 100);
      } else {
        return this._calculatePotentialValue(onlineOrders, onlineOrdersSliderValue);
      }
    } else {
      return revenueTotal / 12 * (onlineOrdersSliderValue / 100) / averageOrderValue;
    }
  }

  _calculateConversionRate(revenueOnline, averageOrderValue, websiteSessionsMthly) {
    return revenueOnline / averageOrderValue / websiteSessionsMthly * 100;
  }

  _calculateOnlineOrders(revenueOnline, averageOrderValue, websiteSessionsMthly, conversionRate) {
    return websiteSessionsMthly 
      ? websiteSessionsMthly * (conversionRate / 100)
      : revenueOnline / averageOrderValue;
  }

  _calculateProfit(revenueOnline, grossProfitMargin) {
    return revenueOnline * (grossProfitMargin / 100);
  }

  _calculatePotentialValue(baseValue, percentChange) {
    return baseValue * (1 + percentChange / 100);
  }

  render() {
    const { revenueOnline, revenueTotal, monthlyRevenueOnline, averageOrderValue, websiteSessionsMthly, conversionRate, onlineOrders, grossProfitMargin, profit } = this.state;
    const { websiteSessionsMthlySliderValue, conversionRateSliderValue, onlineOrdersSliderValue, averageOrderValueSliderValue, grossProfitMarginSliderValue } = this.state;
    const { futureWebsiteSessionsMthly, futureConversionRate, futureOnlineOrders, futureAverageOrderValue, futureRevenueOnline, futureGrossProfitMargin, futureProfit } = this.calculatePotentialValues();

    const layout = {
      showWebsiteSessionsMthly: websiteSessionsMthly > 0 && monthlyRevenueOnline > 0,
      showConversionRate: websiteSessionsMthly > 0 && monthlyRevenueOnline > 0
    };

    return (
      <div className="calculator-summary">
        <h1>Your Digital Growth Potential</h1>
        <p>Check it out! Here is your potential additional revenue and profit based on effectively leveraging digital tools for the growth of your business. If you would like a copy of the results below, and feedback on how this growth is possible for you, fill out the form below.</p>

        <div className="summary-form">
          <Row>
            <Col xs={12} sm={10} smOffset={2} md={11} mdOffset={1}>
              <Row>
                <Col xs={4} md={3} className="section-header text-left">
                  <h2>Your Current</h2>
                </Col>
                <Col xs={4} md={6} className="section-header text-center">
                  <h4>% Increase</h4>
                </Col>
                <Col xs={4} md={3} className="section-header text-right">
                  <h2>Your Potential</h2>
                </Col>
              </Row>
            </Col>
          </Row>

          <Form horizontal>
            {layout.showWebsiteSessionsMthly &&
            <SummaryItem
                title='Monthly Visits'
                value={websiteSessionsMthly}
                sliderValue={websiteSessionsMthlySliderValue}
                onValueChange={(newValue) => this.changeValue({websiteSessionsMthly: newValue})}
                onSliderValueChange={(event, newValue) => this.updateSliderValue({websiteSessionsMthlySliderValue: newValue})}
                potentialValue={futureWebsiteSessionsMthly}
            />}

            {layout.showConversionRate &&
            <SummaryItem
                title='Conversion Rate'
                value={parseFloat(conversionRate.toFixed(2))}
                valueType="percent"
                sliderValue={conversionRateSliderValue}
                onValueChange={(newValue) => this.changeValue({conversionRate: newValue})}
                onSliderValueChange={(event, newValue) => this.updateSliderValue({conversionRateSliderValue: newValue})}
                potentialValue={parseFloat(futureConversionRate.toFixed(2))}
            />}

            <SummaryItem
                title='Online Orders'
                value={Math.round(onlineOrders)}
                sliderValue={onlineOrdersSliderValue}
                showSlider={onlineOrdersSliderValue !== null}
                onValueChange={(newValue) => this.changeValue({onlineOrders: newValue})}
                onSliderValueChange={(event, newValue) => this.updateSliderValue({onlineOrdersSliderValue: newValue})}
                potentialValue={Math.round(futureOnlineOrders)}
            />

            <SummaryItem
                title='Average Order Value'
                value={averageOrderValue}
                valueType={'money'}
                sliderValue={averageOrderValueSliderValue}
                onValueChange={(newValue) => this.changeValue({averageOrderValue: newValue})}
                onSliderValueChange={(event, newValue) => this.updateSliderValue({averageOrderValueSliderValue: newValue})}
                potentialValue={futureAverageOrderValue}
            />
            
            <SummaryItem
                title='Online Revenue'
                value={Math.round(monthlyRevenueOnline)}
                valueType={'money'}
                showSlider={false}
                onValueChange={(newValue) => this.changeValue({revenueOnline: newValue})}
                potentialValue={Math.round(futureRevenueOnline)}
            />
            
            <SummaryItem
                title='Gross Profit Margin'
                value={grossProfitMargin}
                valueType={'percent'}
                sliderValue={grossProfitMarginSliderValue}
                onValueChange={(newValue) => this.changeValue({grossProfitMargin: newValue})}
                onSliderValueChange={(event, newValue) => this.updateSliderValue({grossProfitMarginSliderValue: newValue})}
                potentialValue={parseFloat(futureGrossProfitMargin.toFixed(2))}
            />

            <SummaryItem
                title='Profit per Month'
                value={profit}
                valueType={'money'}
                showSlider={false}
                onValueChange={(newValue) => this.changeValue({profit: newValue})}
                potentialValue={futureProfit}
            />
          </Form>
        </div>

        <div className="signup-form">
          <h3>Start Growing Your eCommerce Revenue</h3>
          <SignupForm
              formUrl={this.props.formUrl}
              thankYouPageUrl={this.props.thankYouPageUrl}
              averageOrderValue={averageOrderValue}
              futureAverageOrderValue={futureAverageOrderValue}
              currentConversionRate={conversionRate}
              futureConversionRate={futureConversionRate}
              websiteSessionsMthly={websiteSessionsMthly}
              futureWebsiteSessionsMthly={futureWebsiteSessionsMthly}
              currentGPM={grossProfitMargin}
              futureGPM={futureGrossProfitMargin}
              currentProfit={profit}
              futureProfit={futureProfit}
              revenueOnline={monthlyRevenueOnline}
              revenueTotal={revenueTotal}
              futureOnlineRevenue={futureRevenueOnline}
              currentOnlineOrdersMthly={onlineOrders}
              futureOnlineOrdersMthly={futureOnlineOrders}
          />
        </div>
      </div>
    );
  }
}

Summary.propTypes = {
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.int,
      timestamp: PropTypes.dateTime
    }).isRequired
  ),
  formUrl: PropTypes.string,
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
      required: PropTypes.bool,
      type: PropTypes.string
    })
  ).isRequired,
  thankYouPageUrl: PropTypes.string
};

export default Summary;